// Arrondi des pourcentages affiches sur les histogrammes de resultats.
//
// Deux incoherences remontees par MEC (mail Garance Abadie du 13/08/2026) :
//   1. le pourcentage global "scores 7-8-9" etait recalcule en additionnant
//      les barres deja arrondies (13 + 33 + 23 = 71 %) au lieu d'arrondir la
//      valeur exacte (21/30 = 70 %), d'ou l'ecart avec l'export Excel ;
//   2. l'ecart d'arrondi (100 - somme des barres) etait reporte en entier sur
//      une seule barre choisie de facon instable, ce qui pouvait decaler une
//      barre de 2 points (33,3 % affiche 35 %) et donner deux pourcentages
//      differents a deux effectifs identiques (7 % et 6 % pour 2 votes).
//
// Regle retenue : deux effectifs egaux affichent toujours le meme pourcentage,
// et aucune barre ne s'ecarte de plus d'un point de sa valeur reelle. Sous ces
// contraintes la somme des barres est ramenee a 100 % (methode du plus fort
// reste) ; dans les rares configurations ou aucune combinaison ne tombe juste
// (ex. six barres a 12,5 %) on s'en approche au plus pres, ce total n'etant de
// toute facon pas affiche. Le pourcentage global scores 7-8-9, lui, n'est plus
// derive des barres : c'est l'arrondi de la valeur exacte.

// Neutralise le bruit flottant avant toute comparaison : 3/30*100 vaut
// 10.000000000000002 en IEEE 754, et 86,666667 - 86 ne donne pas le meme
// double que 6,666667 - 6 alors que les deux barres sont a egalite.
const PRECISION = 1e6;

const normalize = (value) => Math.round(value * PRECISION) / PRECISION;

const remainderOf = (value) => normalize(value - Math.floor(value));

const roundEachToNearest = (raw, keys) => {
  const rounded = {};
  keys.forEach((key) => {
    rounded[key] = Math.round(raw[key]);
  });
  return rounded;
};

/**
 * Arrondit a l'entier un dictionnaire de pourcentages totalisant 100.
 * Renvoie un nouveau dictionnaire, sans muter l'entree.
 */
export function roundPercentagesTo100(percentages) {
  const keys = Object.keys(percentages || {});
  if (keys.length === 0) return {};

  const raw = {};
  const rounded = {};
  keys.forEach((key) => {
    raw[key] = normalize(percentages[key]);
    rounded[key] = Math.floor(raw[key]);
  });

  // Points a distribuer pour retomber sur 100 %.
  const remaining = 100 - keys.reduce((sum, key) => sum + rounded[key], 0);
  if (remaining < 0 || remaining > keys.length) {
    // Les valeurs ne totalisent pas 100 (donnees partielles) : arrondi simple.
    return roundEachToNearest(raw, keys);
  }

  // Groupes de barres de valeur identique : meme effectif => meme pourcentage,
  // donc un groupe est arrondi vers le haut en entier ou pas du tout.
  const groups = new Map();
  keys.forEach((key) => {
    const group = groups.get(raw[key]) || { value: raw[key], keys: [] };
    group.keys.push(key);
    groups.set(raw[key], group);
  });

  const candidates = [...groups.values()]
    .filter((group) => remainderOf(group.value) > 0)
    .sort(
      (a, b) =>
        remainderOf(b.value) - remainderOf(a.value) || // plus fort reste
        b.keys.length - a.keys.length || // puis les groupes les plus larges
        b.value - a.value // a egalite, la plus grande barre
    );

  // Chaque groupe est arrondi vers le haut en entier ou pas du tout. On cherche
  // par programmation dynamique la combinaison qui rapproche le plus la somme
  // de 100 %, et a egalite celle qui s'ecarte le moins des valeurs reelles.
  // Au plus neuf barres : la recherche exhaustive est immediate.
  const best = new Array(keys.length + 1).fill(null);
  best[0] = { score: 0, picked: [] };
  candidates.forEach((group, index) => {
    const size = group.keys.length;
    // 2 x reste - 1 par barre : positif si arrondir le groupe vers le haut
    // rapproche de la valeur reelle, negatif sinon.
    const score = size * (2 * remainderOf(group.value) - 1);
    for (let slot = keys.length; slot >= size; slot -= 1) {
      const from = best[slot - size];
      if (from && (!best[slot] || from.score + score > best[slot].score)) {
        best[slot] = {
          score: from.score + score,
          picked: from.picked.concat(index),
        };
      }
    }
  });

  let chosen = 0;
  best.forEach((entry, slot) => {
    if (!entry) return;
    const gap = Math.abs(remaining - slot);
    const chosenGap = Math.abs(remaining - chosen);
    const closer = gap < chosenGap;
    const asCloseButFiner =
      gap === chosenGap && entry.score > best[chosen].score;
    if (closer || asCloseButFiner) chosen = slot;
  });

  best[chosen].picked.forEach((index) => {
    candidates[index].keys.forEach((key) => {
      rounded[key] += 1;
    });
  });

  return rounded;
}

/**
 * Prepare les resultats d'une assertion pour l'affichage : seules les barres
 * de l'histogramme sont arrondies. percentage_votes_gte_7 et median restent
 * les valeurs exactes du backend, celles de l'export Excel.
 */
export function withRoundedVotePercentages(results) {
  if (!results) return results;
  return {
    ...results,
    percentage_votes: roundPercentagesTo100(results.percentage_votes),
  };
}
